import { Injectable } from '@angular/core';
import { ISalesforceObjectMapping, ISalesforceRelatedObject, SalesforceDataSourceApiService } from 'datasource-service-lib';
import { IEntityPathService, INamedEntity } from 'interfaces-lib';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class SalesforceEntityPathService implements IEntityPathService {
  constructor(private datasourceService: SalesforceDataSourceApiService) {}

  getEntitiesFromPaths(entity: INamedEntity, entityPaths: string[], secondaryEntity?: INamedEntity): Observable<INamedEntity[]> {
    const sourceEntity = secondaryEntity ?? entity;
    const requestedEntityTypes = entityPaths?.length ? entityPaths : [sourceEntity.type];
    const includesSource = requestedEntityTypes.some((path) => this.pathEntityType(path).toLowerCase() === sourceEntity.type.toLowerCase());
    const relatedEntityTypes = requestedEntityTypes
      .map((path) => this.pathEntityType(path))
      .filter((entityType) => entityType.toLowerCase() !== sourceEntity.type.toLowerCase());

    return this.resolveRelatedEntities(sourceEntity, relatedEntityTypes).pipe(
      map((relatedEntities) => {
        const entities = includesSource ? [sourceEntity, ...relatedEntities] : relatedEntities;
        return this.dedupeEntities(entities);
      })
    );
  }

  private resolveRelatedEntities(sourceEntity: INamedEntity, entityTypes: string[]): Observable<INamedEntity[]> {
    if (!entityTypes.length) {
      return of([]);
    }

    return this.datasourceService.getEntityMappings().pipe(
      take(1),
      switchMap((mappings) => {
        const mapping = this.findMapping(mappings, sourceEntity.type);
        const relatedObjects = this.matchRelatedObjects(mapping, entityTypes);
        if (!relatedObjects.length) {
          return of([]);
        }

        return this.datasourceService.get(sourceEntity, { fields: this.buildRelationshipFields(relatedObjects) }).pipe(
          map((record) => this.mapRelatedRecordEntities(record, relatedObjects)),
          catchError(() => of([]))
        );
      })
    );
  }

  private findMapping(mappings: ISalesforceObjectMapping[] | null | undefined, objectName: string): ISalesforceObjectMapping | undefined {
    return (mappings ?? []).find((mapping) => mapping.objectName?.toLowerCase() === objectName?.toLowerCase());
  }

  private matchRelatedObjects(
    mapping: ISalesforceObjectMapping | undefined,
    entityTypes: string[]
  ): ISalesforceRelatedObject[] {
    const requested = entityTypes.map((entityType) => entityType.toLowerCase());
    return (mapping?.relatedObjects ?? []).filter((related) => requested.includes(related.objectName.toLowerCase()));
  }

  private buildRelationshipFields(relatedObjects: ISalesforceRelatedObject[]): string {
    return relatedObjects
      .flatMap((related) => [`${related.relationSegment}.Id`, `${related.relationSegment}.Name`])
      .join(',');
  }

  private mapRelatedRecordEntities(record: any, relatedObjects: ISalesforceRelatedObject[]): INamedEntity[] {
    return relatedObjects
      .map((related) => {
        const relatedRecord = record?.[related.relationSegment];
        if (!relatedRecord?.Id) {
          return undefined;
        }

        return {
          id: relatedRecord.Id,
          type: related.objectName,
          name: relatedRecord.Name ?? relatedRecord.name ?? relatedRecord.Id,
        };
      })
      .filter((entity): entity is INamedEntity => Boolean(entity));
  }

  private pathEntityType(path: string): string {
    return path.split('.').slice(-1)[0];
  }

  private dedupeEntities(entities: INamedEntity[]): INamedEntity[] {
    const seen = new Set<string>();
    return entities.filter((entity) => {
      const key = `${entity.type.toLowerCase()}:${entity.id}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }
}
